/**
 * Fuel & Oil Gauges component for the Aircraft Detail Page.
 *
 * Renders vertical gauge graphics for each tank defined in the aircraft's
 * fuel_tank_config.  Oil is just another tank in the config.
 *
 * New config shape:
 *   { tanks: [ { name: "Left Wing", capacity: 100, unit: "litres" }, ... ] }
 *
 * Readings are stored as tank_0, tank_1, ... or by name in the fuel_oil record.
 *
 * @module fuel-oil-gauges
 */

// ── Pure helpers (exported for testing) ──────────────────

/**
 * Returns colour based on fill ratio.
 * green >= 50%, amber >= 25%, red < 25%.
 * @param {number} ratio - 0 to 1
 * @returns {'green'|'amber'|'red'}
 */
export function getGaugeColour(ratio) {
  if (ratio >= 0.5) return 'green';
  if (ratio >= 0.25) return 'amber';
  return 'red';
}

// ── DOM rendering ────────────────────────────────────────

/**
 * Creates a single vertical gauge element.
 * @param {string} tankName - Display name
 * @param {number} reading - Current reading
 * @param {number} capacity - Max capacity (0 = unknown)
 * @param {string} unit - Unit label
 * @returns {HTMLElement}
 */
function createGauge(tankName, reading, capacity, unit) {
  const pct = capacity > 0 ? Math.min((reading / capacity) * 100, 100) : 0;
  const colour = getGaugeColour(capacity > 0 ? reading / capacity : 0);

  const gauge = document.createElement('div');
  gauge.className = 'fuel-gauge';

  const track = document.createElement('div');
  track.className = 'fuel-gauge__track';

  const fill = document.createElement('div');
  fill.className = `fuel-gauge__fill fuel-gauge__fill--${colour}`;
  fill.style.height = `${pct}%`;
  track.appendChild(fill);

  const levelLabel = document.createElement('span');
  levelLabel.className = 'fuel-gauge__level';
  levelLabel.textContent = capacity > 0 ? `${reading} / ${capacity}` : String(reading);
  track.appendChild(levelLabel);

  gauge.appendChild(track);

  const nameLabel = document.createElement('span');
  nameLabel.className = 'fuel-gauge__name';
  nameLabel.textContent = `${tankName} (${unit})`;
  gauge.appendChild(nameLabel);

  return gauge;
}

// ── Main component ───────────────────────────────────────

/**
 * Creates the Fuel & Oil Gauges container.
 *
 * @param {Object} config
 * @param {Object} config.aircraft - Aircraft record with fuel_tank_config
 * @param {Object|null} config.fuelOilRecord - Most recent fuel/oil readings
 * @param {string|null} config.lastFlightDate - Date of the flight
 * @returns {HTMLElement}
 */
export function createFuelOilGauges(config) {
  const { aircraft, fuelOilRecord, lastFlightDate } = config;

  const container = document.createElement('div');
  container.className = 'fuel-oil-gauges';

  const title = document.createElement('h3');
  title.className = 'fuel-oil-gauges__title';
  title.textContent = 'Fuel & Oil Status';
  container.appendChild(title);

  if (!fuelOilRecord) {
    const noData = document.createElement('p');
    noData.className = 'fuel-oil-gauges__no-data';
    noData.textContent = 'No fuel/oil data recorded yet';
    container.appendChild(noData);
    return container;
  }

  if (lastFlightDate) {
    const dateEl = document.createElement('p');
    dateEl.className = 'fuel-oil-gauges__date';
    dateEl.textContent = `Levels from flight on ${lastFlightDate}`;
    container.appendChild(dateEl);
  }

  const gaugesRow = document.createElement('div');
  gaugesRow.className = 'fuel-oil-gauges__row';

  const tanks = aircraft.fuel_tank_config?.tanks;
  if (Array.isArray(tanks)) {
    tanks.forEach((tank, i) => {
      const reading = fuelOilRecord[`tank_${i}`] ?? fuelOilRecord[tank.name] ?? 0;
      gaugesRow.appendChild(createGauge(
        tank.name,
        Number(reading) || 0,
        Number(tank.capacity) || 0,
        tank.unit || '',
      ));
    });
  }

  container.appendChild(gaugesRow);
  return container;
}
